import { Component, OnInit, Input, ViewChild, AfterViewInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CodemirrorComponent } from 'ng2-codemirror';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectionListChange, MatSelectionList, MatListOption } from '@angular/material/list';
import { TranslateService } from '@ngx-translate/core';
import { VariableValueBrowser } from '../value.browser';
import { VariableBrowser } from '../variable.browser';
import { ValueInputDialog } from '../value.dialog';
import { NgForm } from '@angular/forms';

@Component({
	selector: 'app-task-schedule-variable-content',
	templateUrl: './content.component.html',
	styleUrls: ['./content.component.scss']
})
export class TaskScheduleVariableContentComponent implements OnInit, AfterViewInit {

	_variable = {} as any;
	@Input() set variable(_variable: any) {
		if (_variable) {
			if (this.variableTypes.indexOf(_variable.variableType) === -1) {
				_variable.variableCustomType = _variable.variableType;
				_variable.variableType = 'Custom';
			}
			if (_variable.apiBody) {
				_variable.apiBody = JSON.stringify(_variable.apiBody, null, 2);
			}
			if (_variable.apiColumns) {
				_variable.apiColumns = JSON.stringify(_variable.apiColumns, null, 2);
			}
		}
		this._variable = _variable;
		if (this._variable && !this._variable.associates) {
			this._variable.associates = [];
			if (!this._variable.apiName) {
				this._variable.apiName = this.apiNames[0].id;
			}
			if (!this._variable.apiMethod) {
				this._variable.apiMethod = this.apiMethods[0];
			}
		}
	}
	get variable() {
		return this._variable;
	}
	
	variableTypes = environment.content.variableTypes;
	apiNames = environment.content.apiNames;
	apiMethods = environment.content.apiMethods;
	accessRoles = environment.content.accessRoles;
	activeStates = environment.content.activeStates;

	id: string;

	bodyConfig = {
		lineNumbers: true,
		mode: 'application/json',
		json: true
	};

	@ViewChild('bodyEditor', {static: false}) bodyEditor: CodemirrorComponent;

	columnConfig = {
		lineNumbers: true,
		mode: 'application/json',
		json: true
	};
	@ViewChild('columnEditor', {static: false}) columnEditor: CodemirrorComponent;

	@ViewChild('associates', {static: false}) associates: MatSelectionList;
	
	@ViewChild('taskForm', {static: false}) taskForm: NgForm;

	public invalidBody: string;
	public invalidColumns: string;

	constructor(
		private dialog: MatDialog,
		private translateService: TranslateService
	) {
	}

	ngOnInit() {
	}

	ngAfterViewInit() {
	}

	onBodyChange() {
		this.validateBody();
	}

	onColumnsChange() {
		this.validateColumns();
	}

	isJson(jsonString: string) {
		if (!jsonString || jsonString.length === 0) {
			return true;
		} else {
			try {
				var json = JSON.parse(jsonString);
				return (typeof json === 'object');
			} catch (error) {
				return false;
			}	
		}
	}

	get valid() {
		return this.taskForm.valid;
	}

	validate() {
		if (this.taskForm.controls) {
			for (const control in this.taskForm.controls) {
				this.taskForm.controls[control].markAsTouched();
			}
		}
	}

	validateBody() {
		if (this.variable.apiBody) {
			if (this.variable.apiBody.length > 65535) {
				this.invalidBody = "body-length-invalid";
				return;
			} else if (!this.isJson(this.variable.apiBody)) {
				this.invalidBody = "body-json-invalid";
				return;
			} else {
				this.invalidBody = null;
			}
		}
	}

	validateColumns() {
		if (this.variable.apiColumns) {
			if (this.variable.apiColumns.length > 65535) {
				this.invalidColumns = "columns-length-invalid";
				return;
			} else if (!this.isJson(this.variable.apiColumns)) {
				this.invalidColumns = "columns-json-invalid";
				return;
			} else {
				this.invalidColumns = null;
			}
		}
	}

	_checkAssociateDuplication(variable: any): boolean {
		if (variable.id === this.variable.id) {
			return true;
		} else {
			for (const associate of variable.associates) {
				if (associate.id === this.variable.id) {
					return true;
				} else if (associate.associates) {
					if (this._checkAssociateDuplication(associate)) {
						return true;
					}
				}
			}
			return false;  
		}
	}

	doAddAssociateVariable() {
		const variableSelect = new VariableBrowser(this.dialog);
		
		variableSelect.browse().subscribe((result: any) => {
			if (result.result === true) {
				const variable = result.data[0].getData();

				if (this._checkAssociateDuplication(variable)) {
					this.translateService.get('setting.task.schedule.variable.detail.alert.same-variable-for-association')
					.subscribe((message: string) => alert(message));
				} else {
					this.variable.associates.push(variable);
				}
			}
		});
	}

	onSelectionChangeAssociateVariable($event: MatSelectionListChange) {
		this.associates.options.forEach((option: MatListOption, index: number) => {
			if (option === $event.option) {
				this.variable.associates[index].selected = option.selected;
			}
		});
	}
	
	doDeleteAssociateVariable() {
		this.translateService.get('setting.task.schedule.variable.detail.confirm.delete-selected-associates').subscribe((message: string) => {
			if (confirm(message)) {
				for (let index = this.variable.associates.length - 1; index >= 0; index --) {
					if (this.variable.associates[index].selected) {
						this.variable.associates.splice(index, 1);
					}
				}
			}
		});
	}

	_doBrowseVariableValue(variable: any) {
		if (this.variableTypes.indexOf(variable.variableType) === -1 || variable.variableType === 'Custom') {
			const browser = new VariableValueBrowser(this.dialog, this.apiNames);

			if (variable.associates && variable.associates.length > 0) {
				for (const associate of variable.associates) {
					if (!associate.variableValue) {
						this.translateService.get('setting.task.schedule.variable.detail.alert.missing-associated-variable', { name: associate.name }).subscribe((message: string) => {
							alert(message);
						});
						return;
					}
				}
			}

			if (!this.variable.apiUrl) {
				this.translateService.get('setting.task.schedule.variable.detail.alert.missing-api-url').subscribe((message: string) => {
					alert(message);
				});
			} else {
				browser.browse(variable, variable.variableValue).subscribe(returnValue => {
					if (returnValue.result === true) {
						const variableValue = browser.getValue(variable, returnValue.data);
		
						variable.variableValue = variableValue;
						if (browser.textColumn != null) {
							variable.variableText = browser.getText(variable, returnValue.data);
						} else {
							variable.variableText = variable.variableValue;
						}
	
						this.variable.associates.forEach((associatedVariable: any) => {
							if (associatedVariable !== variable) {
								if (associatedVariable.associates) {
									associatedVariable.associates.forEach((childAssociationVariable: any) => {
										if (childAssociationVariable.id === variable.id) {
											childAssociationVariable.variableValue = variable.variableValue;
											childAssociationVariable.variableText = variable.variableText;
										}
									});
								}
							}
						});
					}
				});  
			}
		} else {
			const dialogRef = this.dialog.open(ValueInputDialog, {
				width: '600px',
				height: '400px',
				data: JSON.parse(JSON.stringify(variable))
			});

			dialogRef.afterClosed().subscribe((result: any) => {
				if (result && result.result) {
					variable.variableText = result.variable.variableText;
					variable.variableValue = result.variable.variableValue;
				}
			});
		}
	}

	doBrowseAssociateVariableValue(associatedVariable: any) {
		this._doBrowseVariableValue(associatedVariable);
	}

	_doTestSetting() {
		this._doBrowseVariableValue(this.variable);
		if (this.variable.apiColumns) {
			this.variable.apiColumns = JSON.stringify(this.variable.apiColumns, null, 2);
		}
	}

	doTestSetting() {
		if (this.variable.apiColumns) {
			if (!(this.variable.apiMethod === 'GET' || this.variable.apiMethod === 'PAGE')) {
				this.translateService.get('setting.task.schedule.variable.detail.confirm.test-with-change-risk', { method: this.variable.apiMethod })
				.subscribe((message: string) => {
					if (confirm(message)) {
						this._doTestSetting();
					}
				});
				return;  
			} else {
				this.variable.apiColumns = JSON.parse(this.variable.apiColumns);
				this._doTestSetting();          
			}
		} else if (this.variable.apiMethod === 'GET' || this.variable.apiMethod === 'PAGE') {
			this.translateService.get('setting.task.schedule.variable.detail.alert.missing-columns', { method: this.variable.apiMethod })
			.subscribe((message: string) => {
				alert(message);
			});
			return;
		} else {
			this.translateService.get('setting.task.schedule.variable.detail.confirm.test-with-change-risk', { method: this.variable.apiMethod })
			.subscribe((message: string) => {
				if (confirm(message)) {
					this._doTestSetting();
				}
			});
			return;
		}
	}
}
